import React, { useCallback, useState } from 'react'
import cx from 'classnames'
import { useMediaContext, useOnRoomReconnecting } from '@gopeerproject/web-kit'

import { Icon, Text } from '@components/UI'

import { Classroom as ClassroomConstants } from '@constants'
import { useClassroomDispatch } from '../hooks'

const ERROR_CODES = {
  UNSTABLE_SIGNALING_CONN: 53001,
  UNSTABLE_MEDIA_CONN: 53405,
  ENDED_VIA_API: 53118
}
const ERROR_CODE_MESSAGES = {
  [ERROR_CODES.UNSTABLE_SIGNALING_CONN]:
    'Your network connection is unstable. Reconnecting your signaling connection!',
  [ERROR_CODES.UNSTABLE_MEDIA_CONN]:
    'Your network connection is unstable. Reconnecting your media connection!',
  /**
   * @ref https://www.twilio.com/docs/api/errors/53118
   */
  [ERROR_CODES.ENDED_VIA_API]:
    'Disconnected because you reached the end time, and overtime was not added. To continue, schedule a new lesson.'
}

const ConnectionBanner = () => {
  const [error, setError] = useState(null)
  const { room } = useMediaContext()
  const classroomDispatch = useClassroomDispatch()

  const onDisconnected = useCallback(
    (reason) => {
      if (reason.code !== ERROR_CODES.ENDED_VIA_API) {
        setError({ type: 'disconnected' })
        return
      }

      setError({ type: 'session_ended', code: reason.code })
      classroomDispatch({
        type: 'set_step',
        payload: ClassroomConstants.STEPS.COMPLETED
      })
    },
    [classroomDispatch]
  )

  const onReconnecting = useCallback((error) => {
    setError({ type: 'reconnecting', code: error.code })
  }, [])

  const onReconnected = useCallback(() => {
    setError(null)
  }, [])

  const close = () => setError(null)

  useOnRoomDisconnected(room, onDisconnected)
  useOnRoomReconnected(room, onReconnected)
  useOnRoomReconnecting(room, onReconnecting)

  if (!error) return null

  return (
    <div
      className={cx('gp-new-classroom-ConnectionBanner', {
        'is-error': error.type === 'disconnected'
      })}
    >
      {error.type === 'disconnected' ? (
        <Text>
          You have been disconnected from the session due to unstable network
          connection.
        </Text>
      ) : (
        <Text>
          {ERROR_CODE_MESSAGES[error.code] ||
            'Your network connection is unstable.'}
        </Text>
      )}
      <div className='control-container dark close-btn'>
        <button onClick={close}>
          <Icon name='close' />
        </button>
      </div>
    </div>
  )
}

const useOnRoomDisconnected = (room, onError) => {
  React.useEffect(() => {
    if (!room) return
    const onDisconnected = (room, error) => {
      if (error) onError(error)
    }

    room.on('disconnected', onDisconnected)
    return () => {
      room.off('disconnected', onDisconnected)
    }
  }, [room, onError])
}

const useOnRoomReconnected = (room, onReconnect) => {
  React.useEffect(() => {
    if (!room) return
    room.on('reconnected', onReconnect)
    return () => {
      room.off('reconnected', onReconnect)
    }
  }, [room, onReconnect])
}

export default ConnectionBanner
